import {Injectable} from '@angular/core';
import {HealthQuestionnaireApiService, TranslationService} from '@emma-labs/shared/angular';
import {
  CategoryCode,
  HealthAnswers,
  ProviderId,
  QuestionId,
  QuestionnaireId,
  QuestionnaireStatus,
  TranslatedUiQuestion,
  translateUiQuestion,
  Version,
} from '@emma-labs/shared/typescript';
import {mergeMap, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class HealthQuestionnaireService {
  constructor(
    private readonly translate: TranslationService,
    private readonly api: HealthQuestionnaireApiService,
    private readonly authService: AuthService,
  ) {}

  getQuestions(
    providerId: ProviderId,
    version: Version,
    healthAnswers: HealthAnswers,
  ): Observable<{
    questions: TranslatedUiQuestion[];
    status: QuestionnaireStatus;
    categoryCode?: CategoryCode;
    extraQuestionnaires?: {
      id: QuestionnaireId;
      questions: TranslatedUiQuestion[];
      status: QuestionnaireStatus;
      title: string;
    }[];
  }> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) => this.api.getQuestions(session.id, providerId, version, healthAnswers, true)),
      map((questionnaire) => {
        return {
          questions: questionnaire.questions.map((question) =>
            translateUiQuestion(question, this.translate.getCurrentLanguageCode()),
          ),
          status: questionnaire.status,
          categoryCode: questionnaire.categoryCode,
          extraQuestionnaires: questionnaire.extraQuestionnaires
            ? questionnaire.extraQuestionnaires.map((eq) => ({
                id: eq.id,
                status: eq.status,
                questions: eq.questions.map((question) =>
                  translateUiQuestion(question, this.translate.getCurrentLanguageCode()),
                ),
                title: eq.title[this.translate.getCurrentLanguageCode()],
              }))
            : undefined,
        };
      }),
    );
  }

  getCategoryCode(
    providerId: ProviderId,
    version: Version,
    answers: HealthAnswers,
  ): Observable<CategoryCode | undefined> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) => this.api.getCategoryCode(session.id, providerId, version, answers)),
    );
  }

  getAllExtraQuestionnaires(
    providerId: ProviderId,
    version: Version,
  ): Observable<{
    extraQuestionnaires: {
      id: QuestionnaireId;
      questions: TranslatedUiQuestion[];
      status: QuestionnaireStatus;
      title: string;
    }[];
  }> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) => this.api.getAllExtraQuestionnaires(session.id, providerId, version)),
      map((questionnaires) => {
        return {
          extraQuestionnaires: questionnaires.extraQuestionnaires.map((eq) => ({
            id: eq.id,
            status: eq.status,
            questions: eq.questions.map((question) =>
              translateUiQuestion(question, this.translate.getCurrentLanguageCode()),
            ),
            title: eq.title[this.translate.getCurrentLanguageCode()],
          })),
        };
      }),
    );
  }

  getExtraQuestionnaires(
    providerId: ProviderId,
    version: Version,
    extraQuestionnaireConfigs: {
      questionnaireId: QuestionnaireId;
      healthAnswers: HealthAnswers;
      referenceQuestionId?: QuestionId;
    }[],
  ): Observable<
    {
      id: QuestionnaireId;
      title: string;
      questions: TranslatedUiQuestion[];
      status: QuestionnaireStatus;
    }[]
  > {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      mergeMap((session) =>
        this.api.getExtraQuestionnaires(session.id, providerId, version, extraQuestionnaireConfigs),
      ),
      map((extraQuestionnaires) =>
        extraQuestionnaires.extraQuestionnaires.map((eq) => ({
          id: eq.id,
          title: eq.title[this.translate.getCurrentLanguageCode()],
          questions: eq.questions.map((question) =>
            translateUiQuestion(question, this.translate.getCurrentLanguageCode()),
          ),
          status: eq.status,
        })),
      ),
    );
  }
}
